import React, { useEffect } from 'react'
import { Box, Button, Stack, Typography, useTheme } from '@mui/material'
import { COLORS } from 'app-constants'
import styled from '@emotion/styled'
import { ReactComponent as GoogleIcon } from './provider-icons/google.svg'
import { capitalizeFirstLetter, getElementColor } from 'utils'
import { AxiosError } from 'axios'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { ReactComponent as AppIcon } from 'components/NavBar/app-icon-dark.svg'
import { ReactComponent as JelledTextDark } from 'components/NavBar/jelled-text-dark.svg'
import { ReactComponent as JelledTextLight } from 'components/NavBar/jelled-text-light.svg'
import gradientPanel from 'components/Login/gradient-panel.svg'
import { useGoogleLogin } from '@react-oauth/google'
import { useFetchToken } from 'hooks/use-auth-token'
import { useSetRecoilState } from 'recoil'
import { featurePermissionsAtomFamily } from 'state/permission'
import { Feature } from 'api/model'
import { useActiveOrganization } from 'hooks/use-organization'
import { usersCheckPermission } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import TagManager from 'react-gtm-module'

const LoginContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`

export interface LoginStateParam {
  redirect_path: string
}

export function isALoginStateParam(obj: any): obj is LoginStateParam {
  return 'redirect_path' in obj
}

const LoginMain = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  let navigate = useNavigate()

  return (
    <>
      <LoginContainer>
        {!isMobile && (
          <Stack
            sx={{
              flex: '1 1 50%',
              height: '100%',
              backgroundImage: `url(${gradientPanel})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              width: '100px',
            }}
          />
        )}

        <Stack
          sx={{
            flex: '1 1 50%',
            height: '100%',
            pl: isMobile ? 0 : 10,
            pt: isMobile ? 0 : 20,
          }}>
          <Stack
            mt={isMobile ? 0 : 10}
            height={isMobile ? '100%' : 'auto'}
            justifyContent={isMobile ? 'center' : 'auto'}
            alignItems={isMobile ? 'center' : 'flex-start'}>
            <a
              href="https://www.jelled.ai"
              style={{
                // position this at the top of the page in the middle only on mobile
                position: isMobile ? 'absolute' : 'relative',
                top: isMobile ? '24px' : '0',
                left: isMobile ? '50%' : '0',
                transform: isMobile ? 'translate(-50%, 0)' : 'none',
                marginBottom: isMobile ? 0 : '84px',
              }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing="8px"
                onClick={() => {
                  navigate('/')
                }}
                sx={{
                  cursor: 'pointer',
                }}>
                <AppIcon
                  style={{
                    width: '26px',
                    height: '26px',
                  }}
                />
                {theme.palette.mode === 'light' ? (
                  <JelledTextDark
                    style={{
                      width: '80px',
                      height: '32px',
                    }}
                  />
                ) : (
                  <JelledTextLight
                    style={{
                      width: '80px',
                      height: '32px',
                    }}
                  />
                )}
              </Stack>
            </a>
            {children}
          </Stack>
        </Stack>
      </LoginContainer>
    </>
  )
}

// this button should be used because it explicitly checks for insights and email responder permissions
export const OnboardingGoogleLoginButton = ({
  acceptedPermissions,
  missingPermissions,
  additionalParams,
}: {
  acceptedPermissions?: string[]
  missingPermissions?: string[]
  additionalParams?: Record<string, string>
}) => {
  const [activeOrganization] = useActiveOrganization()
  const [authCode, setAuthCode] = React.useState<string>('')

  const setInsightsPermissions = useSetRecoilState(featurePermissionsAtomFamily(Feature.insights))
  const setEmailPermissions = useSetRecoilState(featurePermissionsAtomFamily(Feature.email_responder))

  const scopes =
    acceptedPermissions && missingPermissions ? [...acceptedPermissions, ...missingPermissions].join(',') : undefined

  const googleLoginScopes =
    acceptedPermissions && missingPermissions ? [...acceptedPermissions, ...missingPermissions].join(' ') : undefined
  const additionalParamsString = additionalParams
    ? `?${Object.keys(additionalParams)
        .map((key) => `${key}=${additionalParams[key]}`)
        .join('&')}`
    : ''

  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      setAuthCode(codeResponse.code)
    },
    flow: 'auth-code',
    scope: googleLoginScopes,
  })

  // useEffect to detect changes in authCode and reload the page if scopes are present
  const [isLoading, setIsLoading] = React.useState(false)
  useEffect(() => {
    async function fetchData() {
      if (!activeOrganization || isLoading) {
        return
      }

      setIsLoading(true)

      try {
        const insightsResponse = await usersCheckPermission({ feature: Feature.insights })
        const insightsFeaturePermissions = {
          isPermitted: insightsResponse.data.is_permitted,
          acceptedPermissions: insightsResponse.data.accepted_permissions,
          missingPermissions: insightsResponse.data.missing_permissions,
        }
        setInsightsPermissions(insightsFeaturePermissions)

        const emailResponderResponse = await usersCheckPermission({ feature: Feature.email_responder })
        const emailResponderPermissions = {
          isPermitted: emailResponderResponse.data.is_permitted,
          acceptedPermissions: emailResponderResponse.data.accepted_permissions,
          missingPermissions: emailResponderResponse.data.missing_permissions,
        }
        setEmailPermissions(emailResponderPermissions)
      } catch (error) {
        console.log('error in fetching permissions for insights and email responder', error)
      } finally {
        setIsLoading(false)
      }
    }

    // we only want to fetch the new permissions for the feature once authCode is set
    if (authCode && scopes) {
      // the delay is needed because our backend hasn't been updated yet even after authCode is set
      // so we need to wait a bit before fetching the new permissions
      setTimeout(() => {
        fetchData()
      }, 1000)
    }
  }, [authCode, scopes, isLoading, setIsLoading, activeOrganization, setEmailPermissions, setInsightsPermissions])

  useFetchToken(authCode, additionalParamsString, scopes)

  return (
    <Button
      size="medium"
      variant="outlined"
      sx={{
        color: COLORS.GRAY_0,
      }}
      startIcon={<GoogleIcon />}
      onClick={() => login()}>
      Continue with Google
    </Button>
  )
}

export const GoogleLoginButton = ({
  acceptedPermissions,
  missingPermissions,
  additionalParams,
  feature,
}: {
  acceptedPermissions?: string[]
  missingPermissions?: string[]
  additionalParams?: Record<string, string>
  feature: Feature | null
}) => {
  const [activeOrganization] = useActiveOrganization()
  const [authCode, setAuthCode] = React.useState<string>('')

  // if scopes exist in the URL (from an email that the user received), we should use that instead of the googleLoginScopes
  const [searchParams] = useSearchParams()
  const scopesFromUrl = searchParams.get('scopes')
  const decodedScopesFromUrl = decodeURIComponent(scopesFromUrl ?? '').replace(/,/g, ' ')

  const setFeaturePermissions = useSetRecoilState(featurePermissionsAtomFamily(feature ?? ''))

  const setInsightsPermissions = useSetRecoilState(featurePermissionsAtomFamily(Feature.insights))
  const setEmailPermissions = useSetRecoilState(featurePermissionsAtomFamily(Feature.email_responder))

  const scopes =
    acceptedPermissions && missingPermissions ? [...acceptedPermissions, ...missingPermissions].join(',') : undefined

  const googleLoginScopes =
    acceptedPermissions && missingPermissions ? [...acceptedPermissions, ...missingPermissions].join(' ') : undefined
  const additionalParamsString = additionalParams
    ? `?${Object.keys(additionalParams)
        .map((key) => `${key}=${additionalParams[key]}`)
        .join('&')}`
    : ''

  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      setAuthCode(codeResponse.code)
    },
    flow: 'auth-code',
    scope: decodedScopesFromUrl ? decodedScopesFromUrl : googleLoginScopes,
  })

  // useEffect to detect changes in authCode and reload the page if scopes are present
  const [isLoading, setIsLoading] = React.useState(false)
  useEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !feature || isLoading) {
        return
      }

      setIsLoading(true)

      try {
        const response = await usersCheckPermission({ feature })

        // update the atom based on the feature that we're passing in
        const featurePermissions = {
          isPermitted: response.data.is_permitted,
          acceptedPermissions: response.data.accepted_permissions,
          missingPermissions: response.data.missing_permissions,
        }

        if (feature === Feature.insights) {
          setInsightsPermissions(featurePermissions)
        } else if (feature === Feature.email_responder) {
          setEmailPermissions(featurePermissions)
        } else {
          setFeaturePermissions(featurePermissions)
        }
      } catch (error) {
        console.log('error in fetching permissions', error)
      } finally {
        setIsLoading(false)
      }
    }

    // we only want to fetch the new permissions for the feature once authCode is set
    if (authCode && scopes) {
      // the delay is needed because our backend hasn't been updated yet even after authCode is set
      // so we need to wait a bit before fetching the new permissions
      setTimeout(() => {
        fetchData()
      }, 1000)
    }
  }, [
    authCode,
    scopes,
    setFeaturePermissions,
    isLoading,
    setIsLoading,
    activeOrganization,
    feature,
    setEmailPermissions,
    setInsightsPermissions,
  ])

  useFetchToken(authCode, additionalParamsString, scopes)

  return (
    <Button
      size="medium"
      variant="outlined"
      sx={{
        color: COLORS.GRAY_0,
      }}
      startIcon={<GoogleIcon />}
      onClick={() => login()}>
      Continue with Google
    </Button>
  )
}

const Login = () => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()
  // tracks when the user lands on the login page
  const handleConversion = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'conversion',
        send_to: 'AW-16510978365/AcCxCKfQjsEZEL2ShsE9',
      },
    })
  }

  useEffect(() => {
    handleConversion()
  }, [])

  const renderContent = () => {
    return (
      <>
        <Stack justifyContent="flex-end" alignItems="center" spacing={1}>
          <Typography variant={isMobile ? 'h3' : 'h2'}>Welcome to Jelled.ai!</Typography>
        </Stack>

        <Stack spacing={1} mt={4}>
          <GoogleLoginButton feature={null} />
        </Stack>
      </>
    )
  }

  const renderFooter = () => {
    return (
      <Stack mt={4}>
        <Typography
          component={'span'}
          variant="pXS"
          style={{
            width: isMobile ? '240px' : 'auto',
            color: getElementColor(theme.palette.mode === 'dark', theme),
            textAlign: 'center',
          }}>
          By signing in, you agree to our{' '}
          <Box
            sx={{
              display: 'inline-block',
              '&:hover': {
                textDecoration: 'underline',
                textDecorationColor: COLORS.JELLED_PRIMARY_BLUE,
              },
            }}>
            <a
              href="https://www.jelled.ai/terms-of-service"
              style={{ color: COLORS.JELLED_PRIMARY_BLUE, textDecoration: 'none' }}
              target="_blank"
              rel="noreferrer">
              Terms of service
            </a>
          </Box>{' '}
          and{' '}
          <Box
            sx={{
              display: 'inline-block',
              '&:hover': {
                textDecoration: 'underline',
                textDecorationColor: COLORS.JELLED_PRIMARY_BLUE,
              },
            }}>
            <a
              href="https://www.jelled.ai/privacy-policy"
              style={{ color: COLORS.JELLED_PRIMARY_BLUE, textDecoration: 'none' }}
              target="_blank"
              rel="noreferrer">
              Privacy policy
            </a>
          </Box>
        </Typography>
      </Stack>
    )
  }

  return (
    <LoginMain>
      {renderContent()}
      {renderFooter()}
    </LoginMain>
  )
}

const DEFAULT_UNKNOWN_ERROR_TEXT = 'Sorry, an error occurred. Please try again.'

export const errorDialogText: (error: any, fallback?: string) => string = (error: any, fallback?: string) => {
  if (!(error instanceof AxiosError)) {
    return fallback ?? DEFAULT_UNKNOWN_ERROR_TEXT
  }

  const response = error.response?.data
  const validationError = response?.validation_error ?? response.message

  if (!validationError || typeof validationError !== 'string') {
    return fallback ?? DEFAULT_UNKNOWN_ERROR_TEXT
  }

  const authProvider = response?.auth_provider ? `${response?.auth_provider} SSO` : 'SSO'

  switch (validationError) {
    case 'email_invalid':
      return `The email is invalid.  Please try again with a valid email.`

    case 'personal_email_not_allowed':
      return `Looks like you're using a personal email address. Please use your work email to sign up and create an account.`

    case 'email_exists':
      return `Your account already exists in Jelled. Please try signing in.`

    case 'org_exists':
      return `Your domain already exists as an organization in Jelled. Your admin will be contacted to send you an invite.`

    case 'invalid_auth_provider':
      return `Your organization is using ${capitalizeFirstLetter(
        authProvider
      )} for authentication. Please use the SSO option to sign up.`

    case 'password_rejected':
      return `Sorry, your password was not strong enough. Please try again with another.`

    case 'reset_password_code_invalid':
      return `This reset password link has expired. Another email has just been sent, please try again from there.`

    case 'auth_provider_mismatch':
      return `Your account was created using ${capitalizeFirstLetter(
        authProvider
      )} for authentication. Please sign in with ${capitalizeFirstLetter(authProvider)}.`

    default:
      return fallback ?? DEFAULT_UNKNOWN_ERROR_TEXT
  }
}

export const validatePassword = (password: string) => {
  return /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/.test(password)
}

export default Login
